import { ArrowRightOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Spin, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/axios";
import ChatbotPopover from "../components/ChatbotPopover";
import { ChatbotProvider, useChatbot } from "../contexts/ChatContext";
import { FrozenViewProvider } from "../contexts/FrozenViewProvider";
import { useHeader } from "../contexts/HeaderContext";
import { stepLabel } from "../viewLabels";
import { viewComponents } from "./viewComponents";

import styles from "./Review.module.css";

const { Text } = Typography;

function ReviewStage({ learningJourneyId, view }) {
    const { showChatbot } = useChatbot();
    const SpaceToRender = viewComponents[view.view_type];

    if (!SpaceToRender) return null;

    return (
        <div className={styles.stage}>
            <SpaceToRender learningJourneyId={learningJourneyId} view={view} />
            {showChatbot && <ChatbotPopover learningJourneyId={learningJourneyId} view={view} />}
        </div>
    );
}

function Review() {
    const { id: learningJourneyId, stepId } = useParams();
    const navigate = useNavigate();
    const [review, setReview] = useState(null);
    const [reviewedStepId, setReviewedStepId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const cacheRef = useRef(new Map());

    const fetchReview = useCallback((id) => {
        const cached = cacheRef.current.get(id);
        if (cached) return Promise.resolve(cached);

        return api.get(`/workspace/review/${id}/`).then(({ data }) => {
            cacheRef.current.set(id, data);
            return data;
        });
    }, []);

    // Keep the previous step's content on screen while the next one loads,
    // instead of dropping to a spinner between every navigation.
    useEffect(() => {
        let cancelled = false;
        setNotFound(false);
        setLoading(!cacheRef.current.has(stepId));

        fetchReview(stepId)
            .then((data) => {
                if (cancelled) return;
                setReview(data);
                setReviewedStepId(stepId);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to load review:", err);
                setNotFound(true);
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [stepId, fetchReview]);

    useEffect(() => {
        if (!review) return;
        [review.previous?.step_id, review.next?.step_id].filter(Boolean).forEach((id) => {
            if (!cacheRef.current.has(id)) fetchReview(id).catch(() => {});
        });
    }, [review, fetchReview]);

    const { setProgress, setProgressRequested, setCurrentStepId } = useHeader();

    useEffect(() => {
        setProgressRequested(!!review);
        setProgress(review?.subtopic_progress ?? null);
        setCurrentStepId(stepId);

        return () => {
            setProgressRequested(false);
            setCurrentStepId(null);
        };
    }, [review, stepId, setProgress, setProgressRequested, setCurrentStepId]);

    const backToPlan = () => navigate(`/t/${learningJourneyId}/plan`);
    const goToStep = (id) => navigate(`/t/${learningJourneyId}/step/${id}`);
    const continueToLive = () => navigate(`/t/${learningJourneyId}`);

    return (
        <div className={styles.page}>
            <div className={styles.band}>
                {review && (
                    <Text className={styles.location}>
                        {!review.location.included && "This is no longer part of your plan · "}
                        {review.location.topic_name} · {review.location.subtopic_name} ·{" "}
                        {stepLabel(review.location.step_type)}
                    </Text>
                )}
                <div className={styles.rail}>
                    {review?.previous && (
                        <button
                            className={styles.railLink}
                            onClick={() => goToStep(review.previous.step_id)}
                        >
                            <LeftOutlined /> {stepLabel(review.previous.step_type)}
                        </button>
                    )}
                    {review && (
                        review.next ? (
                            <button
                                className={styles.railLink}
                                onClick={() => goToStep(review.next.step_id)}
                            >
                                {stepLabel(review.next.step_type)} <RightOutlined />
                            </button>
                        ) : (
                            <button className={styles.continueLink} onClick={continueToLive}>
                                You're caught up · Continue <ArrowRightOutlined />
                            </button>
                        )
                    )}
                </div>
            </div>

            <div className={`${styles.body} ${loading && review ? styles.stale : ""}`}>
                {notFound ? (
                    <div className={styles.centered}>
                        <Text>This step is no longer available to review.</Text>
                        <button className={styles.backLink} onClick={backToPlan}>
                            <LeftOutlined /> Back to plan
                        </button>
                    </div>
                ) : !review ? (
                    <div className={styles.centered}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <FrozenViewProvider
                        key={reviewedStepId}
                        learningJourneyId={learningJourneyId}
                        view={review.view}
                    >
                        <ChatbotProvider learningJourneyId={learningJourneyId}>
                            <ReviewStage learningJourneyId={learningJourneyId} view={review.view} />
                        </ChatbotProvider>
                    </FrozenViewProvider>
                )}
            </div>
        </div>
    );
}

export default Review;
